import { reactive, ref } from "vue";
import {
  submitCallRequest,
  lookupTargetOperatorId,
  validateIin,
} from "./mockCallApi";

const form = reactive({
  queueType: null,
  organizationId: null,
  notaryUuid: null,
  attorneyIin: "",
  principalPhoneNumber: "",
  requiredFields: [],
});
const submitting = ref(false);
const submitError = ref(null);

const attorneyIinChecking = ref(false);
const attorneyIinServerError = ref("");
const attorneyIinValidated = ref(false);
let attorneyIinCheckSeq = 0;

function resetAttorneyIinValidation() {
  attorneyIinChecking.value = false;
  attorneyIinServerError.value = "";
  attorneyIinValidated.value = false;
}

export function resetCallForm() {
  form.queueType = null;
  form.organizationId = null;
  form.notaryUuid = null;
  form.attorneyIin = "";
  form.principalPhoneNumber = "";
  form.requiredFields = [];
  submitting.value = false;
  submitError.value = null;
  resetAttorneyIinValidation();
}

function isIinValid(iin) {
  return /^\d{12}$/.test(iin);
}

function stripPhoneFormatting(value) {
  const digits = String(value ?? "").replace(/\D/g, "");
  return digits ? "+" + digits : "";
}

function attorneyIinRequired() {
  return form.requiredFields.includes("attorney_iin");
}

async function checkAttorneyIin(selfIin) {
  if (!attorneyIinRequired()) {
    resetAttorneyIinValidation();
    return true;
  }
  const iin = form.attorneyIin;
  if (!isIinValid(iin)) {
    attorneyIinServerError.value = "";
    attorneyIinValidated.value = false;
    return false;
  }
  if (selfIin && iin === String(selfIin)) {
    attorneyIinServerError.value = "sameAsSelf";
    attorneyIinValidated.value = false;
    return false;
  }
  const seq = ++attorneyIinCheckSeq;
  attorneyIinChecking.value = true;
  attorneyIinServerError.value = "";
  try {
    const data = await validateIin(iin);
    if (seq !== attorneyIinCheckSeq) return false;
    if (data && data.valid === true) {
      attorneyIinValidated.value = true;
      return true;
    }
    attorneyIinServerError.value = "invalid";
    attorneyIinValidated.value = false;
    return false;
  } catch (e) {
    if (seq !== attorneyIinCheckSeq) return false;
    attorneyIinServerError.value = "invalid";
    attorneyIinValidated.value = false;
    return false;
  } finally {
    if (seq === attorneyIinCheckSeq) {
      attorneyIinChecking.value = false;
    }
  }
}

function isFormValid() {
  if (!form.queueType || form.organizationId == null) {
    return false;
  }
  if (attorneyIinRequired()) {
    if (!isIinValid(form.attorneyIin)) return false;
    if (!attorneyIinValidated.value) return false;
    if (attorneyIinChecking.value) return false;
  }
  return true;
}

async function submitForm({ principalIin }) {
  if (!isFormValid()) return null;
  submitting.value = true;
  submitError.value = null;

  let targetOperatorId = null;
  if (form.notaryUuid) {
    try {
      targetOperatorId = await lookupTargetOperatorId(form.notaryUuid);
    } catch (e) {
      console.warn(
        "[useCallForm] operator lookup failed; continuing without target_operator_id",
        e
      );
    }
  }

  const payload = {
    service: form.queueType,
    principal_iin: principalIin ?? "",
    principal_phone_number: stripPhoneFormatting(form.principalPhoneNumber),
  };
  if (form.notaryUuid) {
    payload.agent_id = form.notaryUuid;
  }
  if (targetOperatorId) {
    payload.target_operator_id = targetOperatorId;
  }
  if (attorneyIinRequired()) {
    payload.attorney_iin = form.attorneyIin;
  }

  let requestId = null;
  try {
    const data = await submitCallRequest(payload);
    requestId = data?.id || null;
  } catch (e) {
    console.warn(
      "[useCallForm] submitCallRequest failed; advancing to iframe anyway",
      e
    );
  } finally {
    submitting.value = false;
  }

  return { targetOperatorId, queueType: form.queueType, requestId };
}

export function useCallForm() {
  return {
    form,
    submitting,
    submitError,
    resetCallForm,
    isIinValid,
    isFormValid,
    attorneyIinRequired,
    attorneyIinChecking,
    attorneyIinServerError,
    attorneyIinValidated,
    resetAttorneyIinValidation,
    checkAttorneyIin,
    submitForm,
  };
}
